import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface NewsItem {
  id: string;
  title: string;
  content: string | null;
  category: string | null;
  published_at: string | null;
  image_url: string | null;
}

function formatNewsDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export default function NewsSection() {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function fetchNews() {
      try {
        const { data, error } = await supabase
          .from('news')
          .select('id, title, content, category, published_at, image_url')
          .eq('is_published', true)
          .order('published_at', { ascending: false })
          .limit(6);
        if (error) throw error;
        if (!cancelled) setNews((data as NewsItem[]) || []);
      } catch {
        if (!cancelled) setNews([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchNews();
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="mx-auto max-w-6xl px-4 md:px-6 py-14 md:py-20">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">Tin tức &amp; phân tích</h2>
          <p className="mt-2 text-foreground-600">Cập nhật tin tức thị trường vàng mới nhất.</p>
        </div>
        <Link to="/thi-truong" className="text-sm font-semibold text-primary-400 hover:text-primary-300 whitespace-nowrap cursor-pointer">
          Xem tất cả
        </Link>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {!loading && news.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 rounded-xl border border-background-200/60 bg-background-100">
          <div className="w-14 h-14 rounded-full bg-background-200 flex items-center justify-center mb-3">
            <i className="ri-newspaper-line text-2xl text-foreground-500"></i>
          </div>
          <p className="text-sm font-medium text-foreground-700">Chưa có tin tức</p>
          <p className="text-xs text-foreground-500 mt-1">Tin tức thị trường sẽ được cập nhật tại đây</p>
        </div>
      )}

      {!loading && news.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {news.map((item) => (
            <article key={item.id} className="rounded-xl border border-background-200/60 bg-background-100 overflow-hidden hover:border-primary-500/40 transition-colors">
              <div className="h-44 w-full">
                <img
                  src={item.image_url || 'https://readdy.ai/api/search-image?query=Gold%20market%20news%20header%20image%20with%20golden%20bars%20on%20dark%20background%2C%20editorial%20photography&width=800&height=500&seq=news-default&orientation=landscape'}
                  alt={item.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-4 md:p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded-full bg-accent-500/15 text-accent-400 text-xs font-semibold whitespace-nowrap">{item.category || 'Tin tức'}</span>
                  <span className="text-xs text-foreground-600 whitespace-nowrap">{formatNewsDate(item.published_at)}</span>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 leading-snug">{item.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed hidden sm:block">{item.content || ''}</p>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}